import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";


const API_URL = `${API_BASE_URL}/api`;

const CircuitNode = ({ name, designation, employees, reportingRelationships, currentUserId }) => {
  const designationEmployees = employees.filter((emp) => emp.designation === name);
  
  return (
    <div className="circuit-node bg-gray-800 rounded-lg border-2 border-blue-500 shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 p-3 text-white font-bold">
        {name}
        <span className="float-right text-xs bg-blue-900 px-2 py-1 rounded-full">
          L{designation.hierarchyLevel}
        </span>
      </div>
      
      <div className="flex justify-between px-2 py-1 bg-gray-700">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="w-1 h-3 bg-blue-300"></div>
        ))}
      </div>
      
      <div className="p-3 bg-gray-800 flex flex-wrap justify-center">
        {designationEmployees.map((employee) => (
          <EmployeeChip 
            key={employee.id}
            employee={employee} 
            reportingRelationships={reportingRelationships}
            isCurrentUser={employee.id === currentUserId}
          />
        ))}
          
        {designationEmployees.length === 0 && (
          <div className="text-gray-500 text-xs italic">No employees</div>
        )}
      </div>
      
      {designationEmployees.length > 0 && (
        <div className="px-3 pb-3 bg-gray-800">
          <div className="text-sm text-blue-300 mb-2 border-t border-gray-700 pt-2">Reporting Structure:</div>
          {designationEmployees.map(emp => {
            const supervisor = employees.find(e => e.id === emp.reportsTo);
            const reportees = employees.filter(e => e.reportsTo === emp.id);
            const isCurrentUser = emp.id === currentUserId;
            
            return (
              <div key={emp.id} className="text-xs mb-2">
                {supervisor && (
                  <div className="flex items-center gap-1 mb-1">
                    <span className={`${isCurrentUser ? 'text-red-400 font-bold' : 'text-blue-300'}`}>
                      {emp.name} {isCurrentUser && '(You)'}
                    </span>
                    <svg width="16" height="10" className="mx-1">
                      <path d="M0,5 L10,5" stroke="rgba(59, 130, 246, 0.8)" strokeWidth="1.5" />
                      <path d="M10,5 L7,2 L7,8 Z" fill="rgba(59, 130, 246, 0.8)" />
                    </svg>
                    <span className="text-green-300">{supervisor.name}</span>
                  </div>
                )}
                
                {reportees.length > 0 && (
                  <div>
                    <span className={`${isCurrentUser ? 'text-red-400 font-bold' : 'text-purple-300'}`}>
                      {reportees.length} reporting to {emp.name} {isCurrentUser && '(You)'}:
                    </span>
                    <div className="ml-3 mt-1">
                      {reportees.map(reportee => (
                        <div key={reportee.id} className="flex items-center gap-1 mb-1">
                          <span className="text-blue-300">{reportee.name}</span>
                          <svg width="16" height="10" className="mx-1">
                            <path d="M0,5 L10,5" stroke="rgba(59, 130, 246, 0.8)" strokeWidth="1.5" />
                            <path d="M10,5 L7,2 L7,8 Z" fill="rgba(59, 130, 246, 0.8)" />
                          </svg>
                          <span className={`${isCurrentUser ? 'text-red-400 font-bold' : 'text-purple-300'}`}>
                            {emp.name} {isCurrentUser && '(You)'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      <div className="flex justify-between px-2 py-1 bg-gray-700">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="w-1 h-3 bg-blue-300"></div>
        ))}
      </div>
    </div>
  );
};

const EmployeeChip = ({ employee, reportingRelationships, isCurrentUser }) => {
  const reportsTo = reportingRelationships.find(rel => rel.fromId === employee.id);
  const hasReportees = reportingRelationships.some(rel => rel.toId === employee.id);
  
  return (
    <div className={`
      ${isCurrentUser 
        ? 'bg-red-200 text-red-800 border-2 border-red-500 shadow-md' 
        : 'bg-blue-200 text-blue-800'} 
      rounded-full px-3 py-1 text-xs font-semibold inline-block m-1
      ${reportsTo ? 'border-l-4 border-green-500' : ''}
      ${hasReportees ? 'border-r-4 border-purple-500' : ''}
    `}>
      {employee.name} {isCurrentUser && '(You)'}
      {reportsTo && (
        <span className="ml-1 text-green-600" title={`Reports to: ${reportsTo.toName}`}>↑</span>
      )}
      {hasReportees && (
        <span className="ml-1 text-purple-600" title="Has reportees">↓</span>
      )}
    </div>
  );
};

const ReportingArrow = ({ from, to, color = "rgba(59, 130, 246, 0.5)", isCurrentUserArrow = false }) => {
  if (!from || !to) return null;
  
  const startX = from.x + from.width / 2;
  const startY = from.y + from.height / 2;
  const endX = to.x + to.width / 2;
  const endY = to.y + to.height / 2;
  
  const arrowSize = 6;
  const angle = Math.atan2(endY - startY, endX - startX);
  
  const actualEndX = endX - 15 * Math.cos(angle);
  const actualEndY = endY - 15 * Math.sin(angle);
  
  const arrowPath = `
    M ${startX} ${startY}
    L ${actualEndX} ${actualEndY}
  `;
  
  const arrowHead = `
    M ${actualEndX} ${actualEndY}
    L ${actualEndX - arrowSize * Math.cos(angle - Math.PI / 6)} ${actualEndY - arrowSize * Math.sin(angle - Math.PI / 6)}
    L ${actualEndX - arrowSize * Math.cos(angle + Math.PI / 6)} ${actualEndY - arrowSize * Math.sin(angle + Math.PI / 6)}
    Z
  `;
  
  return (
    <>
      <path 
        d={arrowPath} 
        stroke={isCurrentUserArrow ? "rgba(239, 68, 68, 0.7)" : color} 
        strokeWidth={isCurrentUserArrow ? "3" : "2"} 
        fill="none" 
        strokeDasharray={isCurrentUserArrow ? "5,3" : "none"}
      />
      <path 
        d={arrowHead} 
        fill={isCurrentUserArrow ? "rgba(239, 68, 68, 0.7)" : color} 
      />
    </>
  );
};

const StructureView = ({ viewOnly = true }) => {
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [hierarchyOrder, setHierarchyOrder] = useState([]);
  const [reportingRelationships, setReportingRelationships] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeeRefs, setEmployeeRefs] = useState({});
  
  const { user, userType, companyId, logout } = useAuth();
  const currentUserId = user?.id;
  
  const employeeChipRefs = useRef({});
  
  const fetchData = useCallback(async () => {
    if (!companyId) {
      logout();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/employees/structure`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      
      const { designations, employees, hierarchyOrder, reportingRelationships } = response.data;
      
      setDesignations(designations);
      setEmployees(employees);
      setHierarchyOrder(hierarchyOrder);
      setReportingRelationships(reportingRelationships || []);
      setError(null);
    } catch (error) {
      setError("Error fetching data: " + error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
      
      setTimeout(() => {
        calculateEmployeePositions();
      }, 500);
    }
  }, [companyId, userType, logout]);

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
    
    window.addEventListener('resize', calculateEmployeePositions);
    return () => {
      window.removeEventListener('resize', calculateEmployeePositions);
    };
  }, [fetchData, companyId]);
  
  const calculateEmployeePositions = () => {
    const positions = {};
    
    Object.entries(employeeChipRefs.current).forEach(([id, ref]) => {
      if (ref && ref.getBoundingClientRect) {
        const rect = ref.getBoundingClientRect();
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        positions[id] = {
          x: rect.left + scrollLeft,
          y: rect.top + scrollTop,
          width: rect.width,
          height: rect.height
        };
      }
    });
    
    setEmployeeRefs(positions);
  };
  
  const orderedDesignations = hierarchyOrder
    .map((id) => designations.find((d) => d.id === id))
    .filter(Boolean);
  
  const designationsByLevel = orderedDesignations.reduce((acc, designation) => {
    const level = designation.hierarchyLevel;
    if (!acc[level]) acc[level] = [];
    acc[level].push(designation);
    return acc;
  }, {});
  
  const hierarchyLevels = Object.entries(designationsByLevel)
    .sort(([levelA], [levelB]) => Number(levelA) - Number(levelB))
    .map(([level, designations]) => ({
      level: Number(level),
      designations,
    }));

  const currentUserEmployee = employees.find(emp => emp.id === currentUserId);
  const currentUserDesignation = currentUserEmployee?.designation;
  
  if (loading) {
    return (
      <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700 flex justify-center items-center">
        <div className="text-white text-xl">Loading organization structure...</div>
      </div>
    );
  }
  
  if (!companyId) {
    return (
      <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700 flex justify-center items-center">
        <div className="text-white text-xl">Company information not available. Please login again.</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-6 text-center">
          Organization Structure
        </h1>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {currentUserId && (
          <div className="bg-red-100 border border-green-300 text-red-700 p-4 rounded-lg mb-6">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-medium">
                You are logged in as: <span className="text-red-600 font-bold">{currentUserEmployee?.name || user?.name || 'Unknown User'}</span>
                {currentUserDesignation && ` - ${currentUserDesignation}`}
              </p>
            </div>
          </div>
        )}
        
        {viewOnly && (
          <div className="bg-blue-100 border border-blue-300 text-blue-700 p-4 rounded-lg mb-6">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-medium">
                View-only mode: This is the current organizational structure of the company.
              </p>
            </div>
          </div>
        )}
        
        <div className="bg-gray-900 p-6 rounded-xl shadow-lg mb-6">
          <h2 className="text-2xl font-bold text-blue-300 mb-4">Position Summary</h2>
          <div className="w-full overflow-auto max-h-[70vh]">
            <table className="w-full min-w-max border-collapse">
              <thead className="bg-gray-800">
                <tr>
                  <th className="p-3 text-left border border-gray-700 text-blue-200">Position</th>
                  <th className="p-3 text-left border border-gray-700 text-blue-200">Hierarchy Level</th>
                  <th className="p-3 text-left border border-gray-700 text-blue-200">Number of Employees</th>
                  <th className="p-3 text-left border border-gray-700 text-blue-200">Employees</th>
                </tr>
              </thead>
              <tbody>
                {orderedDesignations.map((designation) => {
                  const designationEmployees = employees.filter((emp) => emp.designation === designation.name);
                  const hasCurrentUser = designationEmployees.some(emp => emp.id === currentUserId);
                  
                  return (
                    <tr key={designation.id} className={`hover:bg-gray-800 ${hasCurrentUser ? 'bg-gray-800' : ''}`}>
                      <td className={`p-3 border border-gray-700 ${hasCurrentUser ? 'font-bold text-white' : 'text-gray-300'}`}>
                        {designation.name}
                        {hasCurrentUser && " (Your Position)"}
                      </td>
                      <td className="p-3 border border-gray-700 text-gray-300">{designation.hierarchyLevel}</td>
                      <td className="p-3 border border-gray-700 text-gray-300">{designation.employeeCount || 0}</td>
                      <td className="p-3 border border-gray-700 text-gray-300">
                        {designationEmployees.map(emp => (
                          <span key={emp.id} className={emp.id === currentUserId ? 'text-red-400 font-bold' : ''}>
                            {emp.name}{emp.id === currentUserId ? ' (You)' : ''}
                            {emp !== designationEmployees[designationEmployees.length - 1] ? ', ' : ''}
                          </span>
                        )) || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-gray-900 p-6 rounded-xl shadow-lg relative overflow-hidden">
          <h2 className="text-2xl font-bold mb-6 text-blue-300">Organization Circuit with Reporting Hierarchy</h2>
          
          <div className="mb-4 bg-gray-800 p-3 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-2">Reporting Legend:</h4>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 mr-2"></div>
                <span className="text-gray-300">Reports to someone</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 mr-2"></div>
                <span className="text-gray-300">Has reportees</span>
              </div>
              <div className="flex items-center">
                <svg width="20" height="12" className="mr-2">
                  <path d="M0,6 L15,6" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="2" />
                  <path d="M15,6 L10,3 L10,9 Z" fill="rgba(59, 130, 246, 0.5)" />
                </svg>
                <span className="text-gray-300">Reports to relationship</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 mr-2"></div>
                <span className="text-gray-300">You are here</span>
              </div>
            </div>
          </div>
          
          <div className="absolute inset-0 bg-gray-900 opacity-60 pointer-events-none">
            <div className="w-full h-full" style={{
              backgroundImage: "linear-gradient(rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.05) 1px, transparent 1px)",
              backgroundSize: "20px 20px"
            }}></div>
          </div>
          
          <div className="relative">
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
              {reportingRelationships.map((rel, idx) => {
                const fromPos = employeeRefs[rel.fromId];
                const toPos = employeeRefs[rel.toId];
                const isCurrentUserArrow = rel.fromId === currentUserId || rel.toId === currentUserId;
                
                return (
                  <ReportingArrow 
                    key={`${rel.fromId}-${rel.toId}`} 
                    from={fromPos} 
                    to={toPos}
                    isCurrentUserArrow={isCurrentUserArrow}
                  />
                );
              })}
            </svg>
            
            {hierarchyLevels.map((level, levelIndex) => (
              <div key={level.level} className="mb-16 relative">
                <div className="h-2 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-6 relative">
                  {Array.from({ length: level.designations.length }).map((_, idx) => (
                    <div 
                      key={idx} 
                      className="absolute w-2 h-2 bg-blue-300 rounded-full top-0"
                      style={{ left: `${(idx + 1) * 100 / (level.designations.length + 1)}%` }}
                    ></div>
                  ))}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
                  {level.designations.map((designation, idx) => {
                    const hasCurrentUser = employees.some(emp => 
                      emp.designation === designation.name && emp.id === currentUserId
                    );
                    
                    return (
                      <div key={designation.id} className="relative" style={{ zIndex: 10 }}>
                        {hasCurrentUser && (
                          <div className="absolute -inset-2 bg-red-500 bg-opacity-20 rounded-xl animate-pulse"></div>
                        )}
                        
                        <div 
                          className={`absolute w-px h-6 ${hasCurrentUser ? 'bg-red-400' : 'bg-blue-400'}`}
                          style={{ 
                            left: '50%', 
                            top: '-36px'
                          }}
                        ></div>
                        
                        <CircuitNode
                          name={designation.name}
                          designation={designation}
                          employees={employees}
                          reportingRelationships={reportingRelationships}
                          currentUserId={currentUserId}
                        />
                        
                        {levelIndex < hierarchyLevels.length - 1 && (
                          <div 
                            className={`absolute w-px h-12 ${hasCurrentUser ? 'bg-red-400' : 'bg-blue-400'}`} 
                            style={{ 
                              left: '50%', 
                              bottom: '-16px'
                            }}
                          ></div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {levelIndex < hierarchyLevels.length - 1 && (
                  <div className="absolute w-4 h-4 bg-blue-500 rounded-full left-1/2 transform -translate-x-1/2 -bottom-2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StructureView;